import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSensors } from '../../hooks/useSensors';
import { useUbicacionClima } from '../../hooks/useUbicacionClima';

/**
 * Elegir el punto con el que se consulta el clima.
 *
 * Es la única forma de que un cliente sin sensores —o con campos en dos
 * estados— vea el clima que le sirve. Antes el tablero simplemente no tenía
 * de dónde sacarlo: el KPI de clima mostraba un guion.
 *
 * Se guarda en el SERVIDOR y no en este navegador, a diferencia del resto de
 * esta pantalla. La distinción es real: que alguien prefiera grados
 * Fahrenheit es asunto suyo, pero dónde está el campo es un hecho de la
 * empresa, y todos sus usuarios deben ver el mismo. Guardarlo local
 * significaría que cada quien vea un clima distinto y que cambiar de
 * computadora lo borre — el mismo error que ya se corrigió con las
 * ubicaciones de los sensores.
 */
export default function WeatherLocationCard({ rol }: { rol?: string }) {
  const { t } = useTranslation();
  const { sensors } = useSensors();
  const { ubicacion, configurada, esDeSensores, cargado, guardar } = useUbicacionClima(sensors);

  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [nombre, setNombre] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [listo, setListo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Se rellena una sola vez, cuando llega lo guardado. Sin la guarda, cada
  // renderizado pisaría lo que la persona está escribiendo.
  const [rellenado, setRellenado] = useState(false);
  useEffect(() => {
    if (rellenado || !cargado) return;
    const u = configurada ?? ubicacion;
    if (u) {
      setLat(String(u.lat));
      setLng(String(u.lng));
      setNombre(u.nombre ?? '');
    }
    setRellenado(true);
  }, [cargado, configurada, ubicacion, rellenado]);

  // Sólo los administradores pueden moverlo: cambia lo que ve todo el
  // equipo, no sólo quien lo toca. El servidor lo vuelve a verificar.
  const puedeEditar = rol === 'tenant_admin' || rol === 'super_admin';

  const usarSensores = () => {
    const validos = sensors.filter(s => Number.isFinite(s.lat) && Number.isFinite(s.lng) && (s.lat !== 0 || s.lng !== 0));
    if (validos.length === 0) return;
    setLat((validos.reduce((a, s) => a + s.lat, 0) / validos.length).toFixed(6));
    setLng((validos.reduce((a, s) => a + s.lng, 0) / validos.length).toFixed(6));
    setError(null);
  };

  const onGuardar = async () => {
    const nlat = Number(lat);
    const nlng = Number(lng);
    if (!Number.isFinite(nlat) || Math.abs(nlat) > 90 || !Number.isFinite(nlng) || Math.abs(nlng) > 180) {
      setError(t('weather.settings.invalid'));
      return;
    }
    setError(null);
    setGuardando(true);
    try {
      await guardar({ lat: nlat, lng: nlng, nombre: nombre.trim() });
      setListo(true);
      setTimeout(() => setListo(false), 2000);
    } catch {
      setError(t('weather.settings.failed'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
        {t('weather.settings.title')}
      </div>
      <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 18px' }}>
        <p style={{ fontSize: 12, color: 'var(--gray)', margin: '0 0 14px', lineHeight: 1.6 }}>
          {t('weather.settings.hint')}
        </p>

        {cargado && (
          <div style={{ fontSize: 12, color: 'var(--dark)', marginBottom: 14 }}>
            {configurada
              ? t('weather.settings.current', { name: configurada.nombre || `${configurada.lat.toFixed(4)}, ${configurada.lng.toFixed(4)}` })
              : esDeSensores
                ? t('weather.settings.fromSensors')
                : t('weather.noLocation')}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <label style={{ flex: 1, fontSize: 12, color: 'var(--gray)' }}>
            {t('weather.settings.latitude')}
            <input className="settings-input" value={lat} disabled={!puedeEditar}
                   onChange={e => setLat(e.target.value)} inputMode="decimal" placeholder="27.9691" />
          </label>
          <label style={{ flex: 1, fontSize: 12, color: 'var(--gray)' }}>
            {t('weather.settings.longitude')}
            <input className="settings-input" value={lng} disabled={!puedeEditar}
                   onChange={e => setLng(e.target.value)} inputMode="decimal" placeholder="-101.4074" />
          </label>
        </div>

        <label style={{ display: 'block', fontSize: 12, color: 'var(--gray)', marginBottom: 14 }}>
          {t('weather.settings.name')}
          <input className="settings-input" value={nombre} disabled={!puedeEditar} maxLength={120}
                 onChange={e => setNombre(e.target.value)} placeholder={t('weather.settings.namePlaceholder')} />
        </label>

        {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}

        {puedeEditar && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={onGuardar} disabled={guardando} className="primary-button"
                    style={{ padding: '9px 22px', fontSize: 13, opacity: guardando ? 0.6 : 1 }}>
              {guardando ? t('weather.settings.saving') : listo ? `✓ ${t('weather.settings.saved')}` : t('weather.settings.save')}
            </button>
            {sensors.length > 0 && (
              <button onClick={usarSensores}
                      style={{ padding: '9px 16px', fontSize: 12, background: 'transparent', color: 'var(--green-dk)', border: '1px solid var(--green-bd)', borderRadius: 8, cursor: 'pointer' }}>
                {t('weather.settings.useSensors')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
